import { trackClientEvent } from 'analytics/analytics'
import useLoyaltyOfferPageTargetStayUpgrade from 'components/OfferPage/hooks/useLoyaltyOfferPageTargetStayUpgrade'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ModalContext from 'contexts/ModalContext'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import withMountGuard from 'hocs/withMountGuard'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyTierStayUpgradeMessageBanner from 'loyalty/components/LoyaltyTierStayUpgradeMessageBanner'
import getLoyaltyStayUpgradeEventSubject from 'loyalty/lib/benefits/getLoyaltyStayUpgradeEventSubject'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import IconButton from 'LuxKit/Button/IconButton'
import LoyaltyStayUpgradeStripRow from 'loyalty/components/StayUpgrade/LoyaltyStayUpgradeStripRow'
import LoyaltyStayUpgradeExpandedPanel from 'loyalty/components/StayUpgrade/LoyaltyStayUpgradeExpandedPanel'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import LineFestivalsIcon from 'LuxKit/Icons/line/LineFestivalsIcon'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import TextLink from 'LuxKit/TextLink'
import type { ReactElement } from 'react'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { tierNormalContrastColorStyle } from 'styles/utils/tierStyles'
import { useFunnelFeature } from 'checkout/hooks/useFunnelConfig'

import type { OfferPageLoyaltyStayUpgradeModalResult } from './OfferPageLoyaltyStayUpgradeModal'
import OfferPageLoyaltyStayUpgradeModal from './OfferPageLoyaltyStayUpgradeModal'

/**
 * The chevron is the bar's only action on mobile, so its hit area covers the whole bar rather than
 * the icon alone. Stretching the existing button keeps one interactive element, so the accessible
 * name and keyboard focus stay where they are — a second, wrapping button would nest inside this one.
 */
const WholeBarIconButton = styled(IconButton)`
  ${tierNormalContrastColorStyle}

  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }
`

interface Props {
  packageView: App.OfferPackageOptionView
}

function OfferPageLoyaltyStayUpgradeCallOutBanner({ packageView }: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const loyaltyUpgradeAvailability = packageView.loyaltyUpgradeAvailability
  const { selectedTargetUpgradeType, toggleTargetStayUpgrade } = useLoyaltyOfferPageTargetStayUpgrade(packageView.package)

  // Funnel Optimiser - check if room upgrade is enabled
  const showRoomUpgrade = useFunnelFeature('roomUpgrade')

  const isApplied = !!selectedTargetUpgradeType
  const upgradeType = loyaltyUpgradeAvailability?.upgradeType
  const hasBothTargets = upgradeType === 'room-and-package'
  // The one target this option offers, when it offers only one. Those apply in a single tap; only
  // an option offering both needs the modal to choose between them.
  let singleTargetType: App.LoyaltyStayUpgradeType | undefined
  if (upgradeType === 'room-only') singleTargetType = 'room'
  if (upgradeType === 'package-only') singleTargetType = 'package'

  const applyUpgrade = useCallback((nextUpgradeType: App.LoyaltyStayUpgradeType | undefined) => {
    toggleTargetStayUpgrade(nextUpgradeType)
    trackClientEvent({
      // Removing an upgrade applies `undefined`, so the subject names the currently selected upgrade instead.
      subject: getLoyaltyStayUpgradeEventSubject(nextUpgradeType ?? selectedTargetUpgradeType),
      action: nextUpgradeType ? 'toggled_on' : 'toggled_off',
      category: 'offer_page',
      type: 'interaction',
    })
  }, [toggleTargetStayUpgrade, selectedTargetUpgradeType])

  const onOpenModal = useCallback(async(expandUpgradeType?: App.LoyaltyStayUpgradeType) => {
    const availability = loyaltyUpgradeAvailability
    if (!availability) return

    const openedTypes: Array<App.LoyaltyStayUpgradeType> = []
    if (expandUpgradeType) {
      openedTypes.push(expandUpgradeType)
    } else {
      if (availability.upgradeType === 'room-only' || availability.upgradeType === 'room-and-package') {
        openedTypes.push('room')
      }
      if (availability.upgradeType === 'package-only' || availability.upgradeType === 'room-and-package') {
        openedTypes.push('package')
      }
    }
    openedTypes.forEach((openedUpgradeType) => {
      trackClientEvent({
        subject: getLoyaltyStayUpgradeEventSubject(openedUpgradeType),
        action: 'opened',
        category: 'offer_page',
        type: 'interaction',
      })
    })

    const result = await showModal<OfferPageLoyaltyStayUpgradeModalResult>(<OfferPageLoyaltyStayUpgradeModal
      upgradeAvailabilityView={availability}
      selectedTargetUpgradeType={selectedTargetUpgradeType}
      originPackage={packageView.package}
      initiallyExpandedUpgradeType={expandUpgradeType}
    />)

    if (result) {
      applyUpgrade(result.upgradeType)
    }
  }, [applyUpgrade, loyaltyUpgradeAvailability, packageView.package, selectedTargetUpgradeType, showModal])

  // Both-targets options expand the strip in place rather than opening the modal; the modal is now
  // reached from a `Details` link. Applying collapses the list back to the applied strip.
  const [isExpanded, setIsExpanded] = useState(false)
  const toggleExpanded = useCallback(() => setIsExpanded(current => !current), [])

  const applyPackageUpgrade = useCallback(() => { setIsExpanded(false); applyUpgrade('package') }, [applyUpgrade])
  const applyRoomUpgrade = useCallback(() => { setIsExpanded(false); applyUpgrade('room') }, [applyUpgrade])
  // A strip offering one upgrade — or one already applied — has a single relevant section, so open it
  // expanded. Only a both-kinds strip still being chosen from opens with both headers closed.
  const openDetails = useCallback(
    () => onOpenModal(selectedTargetUpgradeType ?? singleTargetType),
    [onOpenModal, selectedTargetUpgradeType, singleTargetType],
  )
  const openPackageDetails = useCallback(() => onOpenModal('package'), [onOpenModal])
  const openRoomDetails = useCallback(() => onOpenModal('room'), [onOpenModal])

  const onChipClick = useCallback(() => {
    if (isApplied) {
      applyUpgrade(undefined)
      return
    }
    if (hasBothTargets) {
      toggleExpanded()
      return
    }
    if (singleTargetType) applyUpgrade(singleTargetType)
  }, [applyUpgrade, hasBothTargets, isApplied, toggleExpanded, singleTargetType])

  const availabilityView = loyaltyUpgradeAvailability
  const packageTargetName = availabilityView && 'packageUpgradePackage' in availabilityView ? availabilityView.packageUpgradePackage.name : undefined
  const roomTargetName = availabilityView && 'roomUpgradePackage' in availabilityView ?
      (availabilityView.roomUpgradePackage.roomType?.name ?? availabilityView.roomUpgradePackage.name) :
    undefined

  const isBannerVisible = !!showRoomUpgrade && !!loyaltyUpgradeAvailability

  const [impressionRef, impressionInView] = useInView({ triggerOnce: true })
  useEffect(() => {
    if (!impressionInView || !loyaltyUpgradeAvailability) return
    const upgradeTypes: Array<App.LoyaltyStayUpgradeType> = []
    if (upgradeType === 'room-only' || upgradeType === 'room-and-package') {
      upgradeTypes.push('room')
    }
    if (upgradeType === 'package-only' || upgradeType === 'room-and-package') {
      upgradeTypes.push('package')
    }
    upgradeTypes.forEach((impressionUpgradeType) => {
      trackClientEvent({
        subject: getLoyaltyStayUpgradeEventSubject(impressionUpgradeType),
        action: 'impression',
        category: 'offer_page',
        type: 'nonInteraction',
      })
    })
  }, [impressionInView, loyaltyUpgradeAvailability, upgradeType])

  if (!isBannerVisible) return null

  const tierName = getLoyaltyTierDisplayName(tier)
  const programName = getLoyaltyProgramDisplayName()
  const isInfiniteTier = tier === 'infinite'

  let availableMessage: string
  if (singleTargetType === 'room') {
    if (isInfiniteTier) {
      availableMessage = intl.formatMessage(
        {
          id: '_VNGKXS_5eiMeN2crDI0',
          defaultMessage: 'Free room upgrade available with {tierName}.',
        },
        { tierName },
      )
    } else {
      availableMessage = intl.formatMessage(
        {
          id: 'zyJ699D7UkDGHHHaYa0X',
          defaultMessage: 'Free room upgrade available with {program} {tierName}.',
        },
        { program: programName, tierName },
      )
    }
  } else if (singleTargetType === 'package') {
    if (isInfiniteTier) {
      availableMessage = intl.formatMessage(
        {
          id: 'h1I508QpqT5UomVS1KDQ',
          defaultMessage: 'Free package upgrade available with {tierName}.',
        },
        { tierName },
      )
    } else {
      availableMessage = intl.formatMessage(
        {
          id: 'pt-T9CV-BkZobA8-Slgi',
          defaultMessage: 'Free package upgrade available with {program} {tierName}.',
        },
        { program: programName, tierName },
      )
    }
  } else if (isInfiniteTier) {
    availableMessage = intl.formatMessage(
      {
        id: 'XAXZgmdv40eY1NVuqzF5',
        defaultMessage: 'Free upgrade options available with {tierName}.',
      },
      { tierName },
    )
  } else {
    availableMessage = intl.formatMessage(
      {
        id: 'H0aV1YqD1SsyZEi3ti52',
        defaultMessage: 'Free upgrade options available with {program} {tierName}.',
      },
      { program: programName, tierName },
    )
  }

  const appliedMessage = intl.formatMessage({
    id: '5ADIkI0I-vSwDo8018HH',
    defaultMessage: 'Your free upgrade has been applied!',
  })
  const detailsLabel = intl.formatMessage({
    id: 'KX4B1j_FX7527CJFmjJr',
    defaultMessage: 'Details',
  })
  const removeLabel = intl.formatMessage({
    id: 'H7tt8lLY7gtfmauI0eqN',
    defaultMessage: 'Remove',
  })
  const applyLabel = intl.formatMessage({
    id: '52MnR5GMWaxXf5w0r-fr',
    defaultMessage: 'Apply',
  })
  const viewUpgradesLabel = intl.formatMessage({
    id: 'fuRgqRmK_4Ea3XctinkS',
    defaultMessage: 'View upgrades',
  })

  const message = isApplied ? appliedMessage : availableMessage
  // Celebratory leading icon only once an upgrade is on — the available state stays plain.
  const leadingIcon = isApplied ? <LineFestivalsIcon /> : false

  const hideUpgradesLabel = intl.formatMessage({
    id: 'hu5KWRuFW-7ckPPPKKWX',
    defaultMessage: 'Hide upgrades',
  })

  let unappliedChipLabel = applyLabel
  if (hasBothTargets) unappliedChipLabel = isExpanded ? hideUpgradesLabel : viewUpgradesLabel

  const showRows = hasBothTargets && isExpanded && !isApplied
  const isDisclosure = hasBothTargets && !isApplied
  let chipEndIcon: ReactElement | undefined
  if (isApplied) chipEndIcon = <LineTimesIcon />
  else if (isDisclosure) chipEndIcon = <DropdownChevron direction={isExpanded ? 'up' : 'down'} />

  return <div ref={impressionRef}>
    <LoyaltyStayUpgradeExpandedPanel
      tier={tier}
      isExpanded={showRows}
      rows={<>
        {!!packageTargetName && <LoyaltyStayUpgradeStripRow
          upgradeType="package"
          targetName={packageTargetName}
          onApply={applyPackageUpgrade}
          onOpenDetails={openPackageDetails}
        />}
        {!!roomTargetName && <LoyaltyStayUpgradeStripRow
          upgradeType="room"
          targetName={roomTargetName}
          onApply={applyRoomUpgrade}
          onOpenDetails={openRoomDetails}
        />}
      </>}
    >
      <LoyaltyTierStayUpgradeMessageBanner
        tier={tier}
        icon={leadingIcon}
        endContent={<>
          <CSSBreakpoint only="mobile">
            {isDisclosure && <WholeBarIconButton
              className={`tier-${tier}`}
              kind="tertiary"
              size="small"
              aria-label={viewUpgradesLabel}
              onClick={openDetails}
            >
              <LineAngleRightIcon />
            </WholeBarIconButton>}
            {/* Applied and single-target strips keep a pill at every width: both are one-tap actions,
                and a chevron would promise a choice that is not there. */}
            {!isDisclosure && <AssistChip variant="filled" size="small" onClick={onChipClick} endIcon={chipEndIcon}>
              {isApplied ? removeLabel : applyLabel}
            </AssistChip>}
          </CSSBreakpoint>
          <CSSBreakpoint min="tablet">
            <AssistChip
              variant="filled"
              size="small"
              onClick={onChipClick}
              endIcon={chipEndIcon}
              aria-expanded={isDisclosure ? isExpanded : undefined}
            >
              {isApplied ? removeLabel : unappliedChipLabel}
            </AssistChip>
          </CSSBreakpoint>
        </>}
      >
        {!isDisclosure && <span><b>{message}</b> <TextLink weight="regular" size="medium" underline onClick={openDetails}>{detailsLabel}</TextLink></span>}
        {isDisclosure && <>
          <CSSBreakpoint only="mobile">
            <b>{message}</b>
          </CSSBreakpoint>
          <CSSBreakpoint min="tablet">
            <span><b>{message}</b> <TextLink weight="regular" size="medium" underline onClick={openDetails}>{detailsLabel}</TextLink></span>
          </CSSBreakpoint>
        </>}
      </LoyaltyTierStayUpgradeMessageBanner>
    </LoyaltyStayUpgradeExpandedPanel>
  </div>
}

export default withMountGuard(() => useAppSelector(getIsCurrentRegionUserLoyaltyRegion))(
  withOfferPageState(() => ({}), OfferPageLoyaltyStayUpgradeCallOutBanner),
)
